<template>
  <div v-if="alertMessage" class="alert">
    <p>{{ alertMessage }}</p>
  </div>

  <w-layout>
    <w-layout-main full-width>
      <w-layout-main-header>
        <w-stack gap="medium" wrap justify-content="space-between">
          <w-stack-item flex="auto">
            <w-link
              :color-inherit="false"
              @click="handleCancel"
              leading-icon="arrow-left"
            >
              Listagem de Exames
            </w-link>
          </w-stack-item>
          <w-stack-item>
            <w-button
              variant="primary"
              @click="handleSave"
              :label="isEditing ? 'Salvar Alterações' : 'Criar novo exame'"
              :disabled="isSaving"
              v-if="showSaveButton"
            />
          </w-stack-item>
        </w-stack>
      </w-layout-main-header>

      <w-layout-main-content>
        <w-grid>
          <w-grid-item :column-span="12">
            <w-spacer top="medium">
              <w-text type="displayHeading">
                {{ formData.examName || "Novo Exame Laboratorial" }}
              </w-text>
            </w-spacer>

            <w-spacer top="medium" bottom="medium">
              <w-text type="sectionHeading">
                Dados do Exame <span style="color: red">*</span>
              </w-text>
              <w-text type="caption">
                Preencha os campos abaixo para cadastrar um novo exame.
              </w-text>
            </w-spacer>
          </w-grid-item>
        </w-grid>

        <w-grid>
          <w-grid-item :column-span="12">
            <w-card variant="inset">
              <div
                class="card-content"
                :class="{ loading: isSaving || loading }"
              >
                <w-stack gap="medium">
                  <w-select
                    v-model="formData.laboratoryId"
                    label="Laboratório"
                    name="laboratory-select"
                    :options="laboratoryOptions"
                    :error="formErrors.laboratoryId"
                    @change="handleLaboratoryChange"
                    :disabled="isSaving"
                    @input="handleInputChange"
                    placeholder="Selecione um laboratório"
                  />

                  <w-input
                    v-model="formData.examCode"
                    label="Código do Exame"
                    placeholder="Digite o código do exame"
                    type="text"
                    :error="formErrors.examCode"
                    :disabled="isSaving"
                    @input="handleInputChange"
                  />
                </w-stack>

                <w-input
                  v-model="formData.examName"
                  label="Nome do Exame"
                  placeholder="Digite o nome do exame"
                  type="text"
                  :error="formErrors.examName"
                  :disabled="isSaving"
                  @input="handleInputChange"
                />

                <w-stack gap="medium">
                  <w-combobox
                    :key="formData.laboratoryId"
                    v-model="selectedSample"
                    :items="
                      filteredSampleList.map((item) => ({
                        title: item.title || '',
                        value: item.value || '',
                        key: item.key || `sample-${item.value}`,
                      }))
                    "
                    label="Amostra"
                    item-key="key"
                    item-title="title"
                    item-value="value"
                    placeholder="Selecione uma amostra"
                    :return-object="true"
                    :multiple="false"
                    :error="formErrors.sampleId"
                    :required="true"
                  />

                  <w-combobox
                    :key="`proc-${selectedProcedure[0]?.value || 'empty'}`"
                    v-model="selectedProcedure"
                    :items="
                      procedureData
                        .filter((p) => p.id && p.NomeProcedimento)
                        .map((p) => ({
                          title:
                            p.NomeProcedimento?.trim() ||
                            `Procedimento ${p.id}`,
                          value: p.id || '',
                          key: `proc-${p.id}`,
                        }))
                    "
                    label="Procedimento Correspondente"
                    item-key="value"
                    item-title="title"
                    item-value="value"
                    placeholder="Busque ou selecione um procedimento"
                    :searchable="true"
                    @update:search="handleProcedureSearch"
                    :error="formErrors.correspondingProcedureId"
                    :required="true"
                  />
                </w-stack>

                <w-input
                  v-model="formData.resultDays"
                  label="Dias para o Resultado"
                  placeholder="Digite os dias para o resultado"
                  type="number"
                  :error="formErrors.resultDays"
                  :disabled="isSaving"
                  @input="handleInputChange"
                />

                <div v-if="isSaving || loading" class="spinner-overlay">
                  <w-spinner size="large" />
                </div>
              </div>
            </w-card>

            <w-spacer top="medium" bottom="medium">
              <w-text type="sectionHeading">Especificações do exame</w-text>
              <w-text type="caption">Informações adicionais do exame.</w-text>
            </w-spacer>

            <w-card variant="inset">
              <div
                class="card-content"
                :class="{ loading: isSaving || loading }"
              >
                <w-stack gap="medium">
                  <w-input
                    v-model="formData.methodology"
                    label="Metodologia"
                    placeholder="Digite a metodologia"
                    type="text"
                    :disabled="isSaving"
                    @input="handleInputChange"
                  />
                  <w-input
                    v-model="formData.preservative"
                    label="Conservante"
                    placeholder="Digite o conservante"
                    type="text"
                    :disabled="isSaving"
                    @input="handleInputChange"
                  />
                </w-stack>

                <w-stack gap="medium">
                  <w-input
                    v-model="formData.additionalDays"
                    label="Dias Adicionais"
                    placeholder="Digite os dias adicionais"
                    type="number"
                    :disabled="isSaving"
                    @input="handleInputChange"
                  />
                  <w-input
                    v-model="formData.additionalFields"
                    label="Campos Adicionais"
                    placeholder="Digite campos adicionais"
                    type="text"
                    :disabled="isSaving"
                    @input="handleInputChange"
                  />
                </w-stack>

                <!-- Campos Obrigatórios -->
                <w-spacer top="medium" bottom="small">
                  <w-text
                    type="caption"
                    color="secondary"
                    class="toggle-section-title"
                  >
                    Campos Obrigatórios
                  </w-text>
                </w-spacer>

                <w-grid>
                  <w-grid-item :column-span="6">
                    <w-toggle
                      v-model="formData.hasHeight"
                      label="Altura Obrigatória"
                      :disabled="isSaving"
                      @input="handleInputChange"
                    />
                  </w-grid-item>
                  <w-grid-item :column-span="6">
                    <w-toggle
                      v-model="formData.hasWeight"
                      label="Peso Obrigatório"
                      :disabled="isSaving"
                      @input="handleInputChange"
                    />
                  </w-grid-item>
                  <w-grid-item :column-span="6">
                    <w-toggle
                      v-model="formData.hasRegion"
                      label="Região Obrigatória"
                      :disabled="isSaving"
                      @input="handleInputChange"
                    />
                  </w-grid-item>
                  <w-grid-item :column-span="6">
                    <w-toggle
                      v-model="formData.hasVolume"
                      label="Volume Obrigatório"
                      :disabled="isSaving"
                      @input="handleInputChange"
                    />
                  </w-grid-item>
                </w-grid>

                <w-divider />

                <!-- Informações Gestacionais -->
                <w-spacer top="large" bottom="small">
                  <w-text
                    type="caption"
                    color="secondary"
                    class="toggle-section-title"
                  >
                    Informações Gestacionais
                  </w-text>
                </w-spacer>

                <w-grid>
                  <w-grid-item :column-span="6">
                    <w-toggle
                      v-model="formData.includeGestationalAge"
                      label="Idade Gestacional (semanas)"
                      :disabled="isSaving"
                      @input="handleInputChange"
                    />
                  </w-grid-item>
                  <w-grid-item :column-span="6">
                    <w-toggle
                      v-model="formData.includeLastMenstrualDate"
                      label="Data da Última Menstruação (DUM)"
                      :disabled="isSaving"
                      @input="handleInputChange"
                    />
                  </w-grid-item>
                  <w-grid-item :column-span="6">
                    <w-toggle
                      v-model="formData.includeInVitroFertilization"
                      label="Fertilização In Vitro"
                      :disabled="isSaving"
                      @input="handleInputChange"
                    />
                  </w-grid-item>
                  <w-grid-item :column-span="6">
                    <w-toggle
                      v-model="formData.includeMotherAge"
                      label="Idade Materna"
                      :disabled="isSaving"
                      @input="handleInputChange"
                    />
                  </w-grid-item>
                </w-grid>

                <w-divider />

                <!-- Outras Configurações -->
                <w-spacer top="large" bottom="small">
                  <w-text
                    type="caption"
                    color="secondary"
                    class="toggle-section-title"
                  >
                    Outras Configurações
                  </w-text>
                </w-spacer>

                <w-grid>
                  <w-grid-item :column-span="6">
                    <w-toggle
                      v-model="formData.hasObservation"
                      label="Observação no Exame"
                      :disabled="isSaving"
                      @input="handleInputChange"
                    />
                  </w-grid-item>
                  <w-grid-item :column-span="6">
                    <w-toggle
                      v-model="formData.includeCustodyChain"
                      label="Número da Cadeia de Custódia"
                      :disabled="isSaving"
                      @input="handleInputChange"
                    />
                  </w-grid-item>
                  <!-- <w-grid-item :column-span="6">
                    <w-toggle
                      v-model="formData.isCurvedExam"
                      label="Exame de Curva"
                      :disabled="isSaving"
                      @input="handleInputChange"
                    />
                  </w-grid-item> -->
                </w-grid>

                <div v-if="isSaving || loading" class="spinner-overlay">
                  <w-spinner size="large" />
                </div>
              </div>
            </w-card>
          </w-grid-item>
        </w-grid>
      </w-layout-main-content>
    </w-layout-main>
  </w-layout>
</template>

<script setup>
import { FeegowLabApi } from "@/utils/feegow-lab";
import {
  computed,
  defineEmits,
  defineProps,
  nextTick,
  onMounted,
  ref,
  watch,
} from "vue";
import { usePermissions } from "./composables/usePermissions";

const props = defineProps({
  exam: {
    type: Object,
    default: () => ({}),
  },
  loading: Boolean,
});

const isEdited = ref(false);

const emit = defineEmits(["save", "cancel", "change"]);

function handleInputChange() {
  if (!isDataLoaded.value) return;
  isEdited.value = true;
  const field = event.target.name;
  if (formErrors.value[field]) {
    formErrors.value[field] = "";
  }
  emit("change", true);
}

const formData = ref({
  laboratoryId: null,
  examCode: "",
  examName: "",
  sampleId: null,
  sampleName: "",
  materialType: "",
  methodology: "",
  additionalFields: "",
  preservative: "",
  resultDays: null,
  additionalDays: "",
  hasVolume: false,
  hasHeight: false,
  hasWeight: false,
  hasRegion: false,
  hasObservation: false,
  includeGestationalAge: false,
  includeLastMenstrualDate: false,
  includeInVitroFertilization: false,
  includeMotherAge: false,
  includeCustodyChain: false,
  correspondingProcedureId: null,
  correspondingProcedureName: "",
});

const formErrors = ref({
  laboratoryId: "",
  examCode: "",
  examName: "",
  sampleId: "",
  resultDays: "",
  correspondingProcedureId: "",
});

const isEditing = computed(() => !!props.exam?.id);
const originalData = ref({ ...props.exam });

const laboratoryOptions = ref([]);

const sampleData = ref([]);
const procedureData = ref([]);
const isLoadingSamples = ref(false);
const isLoadingProcedures = ref(false);
const isSaving = ref(false);
const selectedSample = ref([]);
const selectedProcedure = ref([]);
const loading = ref(false);
const alertMessage = ref(null);
const isDataLoaded = ref(false);

const filteredSampleList = computed(() => {
  return sampleData.value.map((sample) => ({
    title: sample.title,
    value: sample.value,
    key: `sample-${sample.value}`,
  }));
});

const handleProcedureSearch = debounce(async (query) => {
  isLoadingProcedures.value = true;
  loading.value = true;
  try {
    const labId = formData.value.laboratoryId;
    if (!labId) return;

    const response = await FeegowLabApi.getProcedures({
      query,
      labId: String(labId),
    });

    const validProcedures = response
      .filter((p) => p?.id && p?.NomeProcedimento)
      .map((p) => ({
        id: Number(p.id),
        NomeProcedimento: p.NomeProcedimento.trim(),
      }));

    procedureData.value = [
      ...new Map([
        ...procedureData.value.map((item) => [item.id, item]),
        ...validProcedures.map((item) => [item.id, item]),
      ]).values(),
    ];
  } catch (error) {
    console.error("Erro na busca:", error);
  } finally {
    isLoadingProcedures.value = false;
    loading.value = false;
  }
}, 500);

const { canCreate, canDelete } = usePermissions();

const showSaveButton = computed(() => {
  return (
    (isEditing.value && canDelete.value) ||
    (!isEditing.value && canCreate.value)
  );
});

async function loadLaboratoryOptions() {
  // Laboratórios hardcoded temporariamente
  laboratoryOptions.value = [
    { label: "DB", value: 2, title: "DB" },
    { label: "Álvaro", value: 3, title: "Álvaro" },
    { label: "Hermes Pardini", value: 4, title: "Hermes Pardini" },
    { label: "Shift", value: 5, title: "Shift" },
  ];
}

function debounce(func, delay) {
  let timeoutId;
  return function (...args) {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
    timeoutId = setTimeout(() => {
      func(...args);
    }, delay);
  };
}

onMounted(async () => {
  // Carrega os laboratórios disponíveis
  await loadLaboratoryOptions();
  if (!formData.value.laboratoryId) {
    return;
  }

  loading.value = true;
  isLoadingProcedures.value = true;
  isLoadingSamples.value = true;

  try {
    const labId = formData.value.laboratoryId;

    const [samplesResponse, proceduresResponse] = await Promise.all([
      FeegowLabApi.getSamplesByLab({
        labId: String(labId),
      }),
      FeegowLabApi.getProcedures({
        query: "",
        labId: String(labId),
      }),
    ]);

    sampleData.value = (samplesResponse || [])
      .filter((sample) => sample && sample.id)
      .map((sample) => ({
        title: `${sample.CodAmostra} - ${sample.descamostra}`,
        value: sample.id,
        key: `sample-${sample.id}`,
        CodAmostra: sample.CodAmostra,
        descamostra: sample.descamostra,
      }));
    procedureData.value = (proceduresResponse || [])
      .filter((p) => p?.id && p?.NomeProcedimento)
      .map((p) => ({
        id: Number(p.id),
        NomeProcedimento: p.NomeProcedimento?.trim() || `Proc. ${p.id}`,
        title: p.NomeProcedimento?.trim() || `Proc. ${p.id}`,
        value: Number(p.id),
      }));
  } catch (error) {
    console.error("Erro inicial:", error);
    sampleData.value = [];
    procedureData.value = [];
  } finally {
    isLoadingProcedures.value = false;
    isLoadingSamples.value = false;
    loading.value = false;
  }
});

function validateForm() {
  let isValid = true;
  const errors = {};

  if (!formData.value.laboratoryId) {
    errors.laboratoryId = "Laboratório é obrigatório";
    isValid = false;
  }

  if (!formData.value.examCode.trim()) {
    errors.examCode = "Código do exame é obrigatório";
    isValid = false;
  }

  if (!formData.value.examName.trim()) {
    errors.examName = "Nome do Exame é obrigatório";
    isValid = false;
  }

  if (
    (!selectedSample.value || selectedSample.value.length === 0) &&
    !formData.value.sampleId
  ) {
    errors.sampleId = "Amostra é obrigatória";
    isValid = false;
  }

  if (!selectedProcedure.value || selectedProcedure.value.length === 0) {
    errors.correspondingProcedureId =
      "Procedimento correspondente é obrigatório";
    isValid = false;
  }

  if (!formData.value.resultDays) {
    errors.resultDays = "Dias para o resultado são obrigatórios";
    isValid = false;
  }

  formErrors.value = errors;
  return isValid;
}

function handleCancel() {
  emit("cancel");
}

function showAlert(message) {
  alertMessage.value = message;
  setTimeout(() => {
    alertMessage.value = null;
  }, 4000);
}

async function handleSave() {
  if (validateForm()) {
    isSaving.value = true;
    try {
      const payload = {
        ...formData.value,
        sampleId: selectedSample.value[0].value,
        correspondingProcedureId: formData.value.correspondingProcedureId,
        hasObservation: Boolean(formData.value.hasObservation),
        hasVolume: Boolean(formData.value.hasVolume),
        hasHeight: Boolean(formData.value.hasHeight),
        hasWeight: Boolean(formData.value.hasWeight),
        hasRegion: Boolean(formData.value.hasRegion),
        includeGestationalAge: Boolean(formData.value.includeGestationalAge),
        includeLastMenstrualDate: Boolean(
          formData.value.includeLastMenstrualDate
        ),
        includeInVitroFertilization: Boolean(
          formData.value.includeInVitroFertilization
        ),
        includeMotherAge: Boolean(formData.value.includeMotherAge),
        includeCustodyChain: Boolean(formData.value.includeCustodyChain),
      };

      delete payload.sampleName;
      delete payload.correspondingProcedureName;

      let response;
      if (isEditing.value) {
        payload.examId = props.exam.id;
        payload.oldCorrespondingProcedureId =
          originalData.value.correspondingProcedureId;
        payload.newCorrespondingProcedureId =
          formData.value.correspondingProcedureId;
        delete payload.correspondingProcedureId;
        response = await FeegowLabApi.updateExamLicense(payload);
      } else {
        response = await FeegowLabApi.insertExamLicense(payload);
      }

      if (response.success) {
        showAlert(
          isEditing.value
            ? "Exame atualizado com sucesso!"
            : "Exame cadastrado com sucesso!"
        );
        emit("save", response);
        resetForm();
      } else {
        throw new Error(response.message || "Erro desconhecido no servidor.");
      }
    } catch (error) {
      showAlert(
        error.message || "Erro ao salvar o exame. Por favor, tente novamente."
      );
    } finally {
      isSaving.value = false;
    }
  }
}

function resetForm() {
  formData.value = {
    laboratoryId: null,
    examCode: "",
    examName: "",
    sampleId: null,
    sampleName: "",
    materialType: "",
    methodology: "",
    additionalFields: "",
    preservative: "",
    resultDays: null,
    additionalDays: "",
    hasVolume: false,
    hasHeight: false,
    hasWeight: false,
    hasRegion: false,
    hasObservation: false,
    includeGestationalAge: false,
    includeLastMenstrualDate: false,
    includeInVitroFertilization: false,
    includeMotherAge: false,
    includeCustodyChain: false,
    correspondingProcedureId: null,
    correspondingProcedureName: "",
  };

  selectedSample.value = [];
  sampleData.value = [];
  procedureData.value = [];
}

async function loadAllRequiredData(examId, labId) {
  loading.value = true;
  isDataLoaded.value = false;
  try {
    const [examData, proceduresData, samplesData] = await Promise.all([
      examId ? FeegowLabApi.fetchExamDetails(examId) : Promise.resolve(null),
      labId
        ? FeegowLabApi.getProcedures({ query: "", labId: String(labId) })
        : FeegowLabApi.getProcedures({ query: "" }),
      labId
        ? FeegowLabApi.getSamplesByLab({ labId: String(labId) })
        : Promise.resolve([]),
    ]);

    procedureData.value = (proceduresData || [])
      .filter((p) => p?.id && p?.NomeProcedimento)
      .map((p) => ({
        id: Number(p.id),
        NomeProcedimento: p.NomeProcedimento?.trim() || `Proc. ${p.id}`,
        title: p.NomeProcedimento?.trim() || `Proc. ${p.id}`,
        value: Number(p.id),
      }));

    sampleData.value = (samplesData || [])
      .filter((sample) => sample && sample.id)
      .map((sample) => ({
        title: `${sample.CodAmostra} - ${sample.descamostra}`,
        value: sample.id,
        key: `sample-${sample.id}`,
        CodAmostra: sample.CodAmostra,
        descamostra: sample.descamostra,
      }));

    return examData;
  } catch (error) {
    console.error("Erro ao carregar dados:", error);
    throw error;
  } finally {
    loading.value = false;
    isDataLoaded.value = true;
  }
}

watch(
  () => props.exam,
  async (newExam) => {
    if (newExam && Object.keys(newExam).length > 0) {
      loading.value = true;
      let foundProcedure = null;

      try {
        const sampleId = newExam.amostraID ? parseInt(newExam.amostraID) : null;
        const labId = newExam.labID ? Number(newExam.labID) : null;

        await loadAllRequiredData(newExam.id, labId);

        if (labId) {
          const foundSample = sampleData.value.find(
            (s) => s.value === sampleId
          );
          if (foundSample) {
            selectedSample.value = [
              {
                title: foundSample.title,
                value: foundSample.value,
                key: `sample-${foundSample.value}`,
              },
            ];
            formData.value.sampleId = foundSample.value;
            formData.value.sampleName = foundSample.title;
          }
        }

        const procedureId = Number(newExam.correspondingProcedureId) || null;
        if (procedureId) {
          foundProcedure = procedureData.value.find(
            (p) => Number(p.id) === procedureId
          );

          if (!foundProcedure) {
            const procedureName =
              newExam.correspondingProcedureName?.trim() ||
              `Procedimento ${procedureId}`;
            foundProcedure = {
              id: procedureId,
              NomeProcedimento: procedureName,
              title: procedureName,
              value: procedureId,
              key: `proc-${procedureId}`,
              __isFallback: true,
            };
            procedureData.value = procedureData.value.concat(foundProcedure);
            await nextTick();
          }

          selectedProcedure.value = foundProcedure
            ? [
                {
                  title:
                    foundProcedure.NomeProcedimento?.trim() ||
                    `Procedimento ${foundProcedure.id}`,
                  value: foundProcedure.id,
                  key: `proc-${foundProcedure.id}`,
                },
              ]
            : [];
        }

        formData.value = {
          laboratoryId: labId,
          examCode: newExam.codExame || "",
          examName: newExam.nomeExame || "",
          sampleId: sampleId,
          sampleName: newExam.sampleName || "",
          materialType: newExam.tipoMaterial || "",
          methodology: newExam.metodologia || "",
          additionalFields: newExam.camposAdicionais || "",
          preservative: newExam.conservante || "",
          resultDays: newExam.diasParaResultado || null,
          additionalDays: newExam.diasAdicionais || "",
          hasVolume: newExam.camposObrigatorios?.volume || false,
          hasHeight: newExam.camposObrigatorios?.altura || false,
          hasWeight: newExam.camposObrigatorios?.peso || false,
          hasRegion: newExam.camposObrigatorios?.regiao || false,
          hasObservation: newExam.ObsExame || false,
          includeGestationalAge: newExam.IdadeGestacional || false,
          includeLastMenstrualDate: newExam.ultimaMenstruacaoData || false,
          includeInVitroFertilization: newExam.FertilizacaoInVitro || false,
          includeMotherAge: newExam.IdadeMae || false,
          includeCustodyChain: newExam.CadeiaCustodia || false,
          correspondingProcedureId: procedureId,
          correspondingProcedureName: foundProcedure?.NomeProcedimento || "",
        };

        originalData.value = { ...formData.value };
      } catch (error) {
        console.error("Erro ao carregar dados do exame:", error);
        showAlert(
          "Erro ao carregar dados do exame. Por favor, tente novamente."
        );
      } finally {
        loading.value = false;
      }
    } else {
      resetForm();
    }
  },
  { immediate: true, deep: true }
);

watch(selectedSample, (newVal) => {
  if (formErrors.value.sampleId && newVal && newVal.length > 0) {
    formErrors.value.sampleId = "";
  }

  if (newVal && newVal.length > 0) {
    const sample = newVal[0];
    formData.value.sampleId = sample.value;
    formData.value.sampleName = sample.title;
  } else {
    formData.value.sampleId = null;
    formData.value.sampleName = "";
  }
});

watch(selectedProcedure, (newVal) => {
  if (newVal && newVal.length > 0) {
    formData.value.correspondingProcedureId = newVal[0]?.value || null;
    formData.value.correspondingProcedureName = newVal[0]?.title || "";
  } else {
    formData.value.correspondingProcedureId = null;
    formData.value.correspondingProcedureName = "";
  }
});

async function handleLaboratoryChange(event) {
  try {
    selectedSample.value = [];
    sampleData.value = [];
    procedureData.value = [];

    const labId =
      event?.target?.value !== undefined ? event.target.value : event;

    if (!labId) return;

    isLoadingSamples.value = true;
    loading.value = true;

    const [samplesResponse, proceduresResponse] = await Promise.all([
      FeegowLabApi.getSamplesByLab({
        labId: String(labId),
      }),
      FeegowLabApi.getProcedures({
        query: "",
        labId: String(labId),
      }),
    ]);

    // Processa dados das amostras
    sampleData.value = (samplesResponse || [])
      .filter((sample) => sample && sample.id)
      .map((sample) => ({
        title: `${sample.CodAmostra} - ${sample.descamostra}`,
        value: sample.id,
        key: `sample-${sample.id}`,
        CodAmostra: sample.CodAmostra,
        descamostra: sample.descamostra,
      }));

    // Processa dados dos procedimentos
    procedureData.value = (proceduresResponse || [])
      .filter((p) => p?.id && p?.NomeProcedimento)
      .map((p) => ({
        id: Number(p.id),
        NomeProcedimento: p.NomeProcedimento?.trim() || `Proc. ${p.id}`,
        title: p.NomeProcedimento?.trim() || `Proc. ${p.id}`,
        value: Number(p.id),
      }));

    await nextTick();

    if (isEditing.value && props.exam?.amostraID) {
      const initialSample = sampleData.value.find(
        (s) => s.value === Number(props.exam.amostraID)
      );

      if (initialSample) {
        selectedSample.value = [
          {
            title: initialSample.title,
            value: initialSample.value,
            key: `sample-${initialSample.value}`,
          },
        ];
        formData.value.sampleId = initialSample.value;
        formData.value.sampleName = initialSample.title;
      }

      if (props.exam?.correspondingProcedureId) {
        const procedureId = Number(props.exam.correspondingProcedureId);
        const foundProcedure = procedureData.value.find(
          (p) => Number(p.id) === procedureId
        );

        if (foundProcedure) {
          selectedProcedure.value = [
            {
              title:
                foundProcedure.NomeProcedimento?.trim() ||
                `Proc. ${foundProcedure.id}`,
              value: foundProcedure.id,
              key: `proc-${foundProcedure.id}`,
            },
          ];
        }
      }
    }
  } catch (error) {
    console.error("Erro ao carregar dados:", error);
    sampleData.value = [];
    selectedSample.value = [];
    procedureData.value = [];
  } finally {
    isLoadingSamples.value = false;
    loading.value = false;
  }
}

watch(
  () => formData.value.laboratoryId,
  async (newLabId) => {
    if (!newLabId) {
      selectedSample.value = [];
      selectedProcedure.value = [];
      sampleData.value = [];
      procedureData.value = [];
      return;
    }

    // Se não estamos editando, carrega os dados automaticamente
    if (!isEditing.value) {
      await handleLaboratoryChange(newLabId);
    }
  },
  { immediate: true }
);
</script>

<style scoped>
.alert {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 10000;
  padding: 1rem;
  background-color: var(--w-color-success-600);
  color: var(--w-color-white);
  text-align: center;
}

.card-content {
  position: relative;
  min-height: 100px;
  padding: var(--w-spacing-4);
}

.card-content.loading {
  pointer-events: none;
  opacity: 0.7;
}

.spinner-overlay {
  position: absolute;
  inset: 0;
  background: rgba(255, 255, 255, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: var(--w-z-index-overlay);
}

:deep(.w-input),
:deep(.w-select),
:deep(.w-combobox) {
  width: 100%;
}

:deep(.w-toggle) {
  padding: var(--w-spacing-3) var(--w-spacing-2);
  margin-bottom: var(--w-spacing-2);
  border-radius: var(--w-border-radius-medium);
  background-color: var(--w-color-background-secondary);
  transition: all 0.2s ease;
}

:deep(.w-toggle:hover) {
  background-color: var(--w-color-background-tertiary);
}

:deep(.w-toggle[aria-checked="true"]) {
  background-color: var(--w-color-primary-50);
}

.toggle-section-title {
  font-weight: var(--w-font-weight-semibold);
  color: var(--w-color-text-secondary);
  margin-bottom: var(--w-spacing-2);
}

@media (max-width: 768px) {
  .w-stack[justify-content="space-between"] {
    flex-wrap: wrap;
    gap: var(--w-spacing-4);
  }

  .w-link {
    flex: 1 1 100%;
  }

  .w-button[variant="primary"] {
    width: 100%;
  }

  .card-content {
    padding: var(--w-spacing-3);
  }

  /* Layout responsivo dos toggles em telas menores */
  :deep(.w-grid-item[column-span="6"]) {
    width: 100%;
  }

  :deep(.w-toggle) {
    margin-bottom: var(--w-spacing-3);
  }
}

@media (pointer: coarse) {
  :deep(.w-input input),
  :deep(.w-select select),
  :deep(.w-combobox input) {
    min-height: 48px;
    font-size: 16px;
  }
}
</style>
